import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api } from '@/core/api'
import { logger } from '@/utils/logger'

export type UserProvider = {
  id: string
  provider: string
  providerId?: string
  username?: string
  name?: string
  email?: string
  avatarUrl?: string
  isTokenValid?: boolean
  authMethod?: string
  customBaseUrl?: string
  connectedAt?: string
  /** @deprecated use username */
  provider_username?: string
  /** @deprecated use providerId */
  provider_user_id?: string
  /** @deprecated use authMethod */
  auth_method?: string
}

export type User = {
  id: string
  email: string
  name: string
  avatarUrl?: string
  plan?: string
  paddleCustomerId?: string
  usageModeEnabled?: boolean
  usageMonthlyLimitBcu?: number | null
  usageSpentThisPeriodBcu?: number
  usagePeriodStart?: string
  createdAt?: string
  providers?: UserProvider[]
  availableProviders?: string[]
  availableGitHubAuthMethods?: string[]
  availableGitLabAuthMethods?: string[]
  githubAppInstallationUrl?: string
  /** @deprecated mapped from avatarUrl for compatibility */
  avatar_url?: string
  /** @deprecated mapped from plan for compatibility */
  subscription?: {
    plan: string
    status: string
    bcu_balance?: number
    bcu_limit?: number
  }
  [key: string]: unknown
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const loading = ref(false)

  const isAuthenticated = computed(() => !!user.value)

  async function fetchProfile() {
    loading.value = true
    try {
      const data = await api.getCurrentUser() as User

      // Normalize: backend returns camelCase avatarUrl, some components may use avatar_url
      if (data.avatarUrl && !data.avatar_url) {
        data.avatar_url = data.avatarUrl
      }

      // Normalize: backend returns plan as top-level field, some components may use subscription.plan
      if (data.plan && !data.subscription) {
        data.subscription = {
          plan: data.plan,
          status: 'active',
        }
      }

      user.value = data
      return data
    } catch (error) {
      logger.error('AuthStore', 'Failed to fetch profile:', error)
      user.value = null
      throw error
    } finally {
      loading.value = false
    }
  }

  function setToken(token: string | null) {
    api.setToken(token)
  }

  async function logout() {
    try {
      await api.logout()
    } catch (error) {
      logger.error('AuthStore', 'Logout error:', error)
    } finally {
      api.setToken(null)
      user.value = null
    }
  }

  function $reset() {
    user.value = null
    loading.value = false
  }

  return {
    user,
    loading,
    isAuthenticated,
    fetchProfile,
    setToken,
    logout,
    $reset,
  }
})
